import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Truck, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@workspace/ui/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getDistanceUnitPlural, isMetricCountry } from "@/lib/api";
import type { DeliveryAirport } from "@/lib/api";
import { matchDeliveryAirport } from "@/lib/airport-match";
import { formatPrice } from "@workspace/api-client-react";
import { MobileAwareAutocomplete } from "@/components/search/MobileAwareAutocomplete";
import { addressSuggestionLines, optionalString } from "@/lib/address-suggestion-label";

const MOBILE_WEB_QUERY = "(max-width: 1023px)";
const isMobileWeb = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MOBILE_WEB_QUERY).matches;
/** Mobile web opts into venue-preserving booking results; desktop is unchanged. */
function addressSearchUrl(q: string): string {
  return `${API_BASE}/address/search?q=${encodeURIComponent(q)}${isMobileWeb() ? "&context=booking" : ""}`;
}

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export interface CheckoutLocationDraft {
  deliveryDistance: number | null;
  deliveryAirportCode: string | null;
  deliveryAddress: string | null;
  /**
   * Task #2609 — geocoded coordinates of the selected delivery address.
   * Null for self-pickup and airport-delivery (the host map uses the
   * airport coords / car coords for those paths). When non-null, the
   * checkout flow forwards these to POST /bookings so the host's
   * "Delivery location" map pin lands on the typed address instead of
   * falling back to the car's home location.
   */
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
}

interface Props {
  trigger: React.ReactNode;
  pickupCity: string;
  pickupState: string;
  carLatitude: number | null;
  carLongitude: number | null;
  countryCode: string | null;
  currency: string;
  deliveryEnabled: boolean;
  deliveryMaxRadius: number | null;
  deliveryAirports: DeliveryAirport[];
  /** Current selection, undefined means free pickup */
  currentDistance: number | null;
  currentAirportCode: string | null;
  currentAddress: string | null;
  /** Explicit mode avoids inferring delivery from address text alone. */
  currentIsDelivery?: boolean;
  /**
   * Task #2609 — coords of the currently-selected delivery address (if
   * any). Threaded back in so reopening the editor and pressing Apply
   * without re-selecting a suggestion preserves the persisted pin
   * instead of clearing it.
   */
  currentLatitude?: number | null;
  currentLongitude?: number | null;
  onApply: (next: CheckoutLocationDraft) => void;
  /**
   * Task #2420 — when true, the pick-up ⇄ delivery mode toggle is
   * disabled and a short note explains the renter must message the
   * owner to switch modes. The renter can still edit the address /
   * airport within the current mode (which doesn't change the
   * delivery_fee structure on the host's side). Used by the renter
   * side of the "Request a change" modal so the host's prior fee
   * agreement is preserved while still letting the renter correct a
   * typo or pick a different airport.
   */
  lockMode?: boolean;
  /** Directional mode permissions for change-request flows. */
  allowPickupMode?: boolean;
  allowDeliveryMode?: boolean;
  /** Additional explanation for a direction that is unavailable. */
  modeEligibilityNote?: string;
  /**
   * Task #2664 — when true, the editor forces delivery mode: the
   * "Pick up myself" tile is disabled (same visual treatment as
   * `lockMode`), the editor opens in delivery mode regardless of the
   * `current*` props, and a short note explains the constraint. Used
   * by the host-side Build Your Quote modal for event bookings where
   * delivery is mandatory (mirrors the listing wizard / renter-side
   * checkout rule).
   */
  requireDelivery?: boolean;
  /**
   * Optional override for the note shown when `requireDelivery` is true.
   * Defaults to the event-hire copy. The host-side "Build a trip" modal
   * uses this so chauffeured trips (also delivery-only) get accurate
   * wording instead of "Event hires require delivery."
   */
  requireDeliveryNote?: string;
  /**
   * When true, the tile labels switch to the neutral host-facing wording
   * ("Pick up" / "Deliver") instead of the renter's voice ("Pick up
   * myself" / "Deliver to me"). Defaults off so renter checkout, the
   * renter's "Request a change" modal, and the trip detail page keep the
   * existing copy. Turned on only by the host-side "Build a trip" quote
   * builder, where the host — not the renter — is assembling the quote.
   * No behavioural / pricing change; copy only.
   */
  neutralLabels?: boolean;
  /** Copy only; opt in for host changes without affecting neutral quote builders. */
  audience?: "renter" | "host";
}

function haversine(lat1: number, lon1: number, lat2: number, lon2: number, useKm: boolean): number {
  const R = useKm ? 6371.0 : 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function fetchDrivingDistance(
  originLat: number,
  originLon: number,
  destLat: number,
  destLon: number,
  countryCode: string,
  useKm: boolean,
): Promise<{ distance: number; fallback: boolean }> {
  try {
    const sp = new URLSearchParams({
      originLat: String(originLat),
      originLon: String(originLon),
      destLat: String(destLat),
      destLon: String(destLon),
      countryCode,
    });
    const res = await fetch(`${API_BASE}/delivery/distance?${sp.toString()}`);
    if (res.ok) {
      const data = await res.json();
      if (typeof data.distance === "number" && data.distance >= 0) {
        return { distance: data.distance, fallback: !!data.fallback };
      }
    }
  } catch {
    // Fall through to local haversine
  }
  const dist = haversine(originLat, originLon, destLat, destLon, useKm);
  return { distance: Math.round(dist * 10) / 10, fallback: true };
}

export function CheckoutLocationEditor({
  trigger,
  pickupCity,
  pickupState,
  carLatitude,
  carLongitude,
  countryCode,
  currency,
  deliveryEnabled,
  deliveryMaxRadius,
  deliveryAirports,
  currentDistance,
  currentAirportCode,
  currentAddress,
  currentIsDelivery,
  currentLatitude = null,
  currentLongitude = null,
  onApply,
  lockMode = false,
  allowPickupMode,
  allowDeliveryMode,
  modeEligibilityNote,
  requireDelivery = false,
  requireDeliveryNote,
  neutralLabels = false,
  audience = "renter",
}: Props) {
  const { t } = useTranslation();
  const isHost = audience === "host";
  const [open, setOpen] = useState(false);

  const [mode, setMode] = useState<"pickup" | "delivery">(
    requireDelivery || (currentIsDelivery ?? (!!currentAirportCode || (currentDistance != null && currentDistance > 0)))
      ? "delivery"
      : "pickup",
  );
  const [airportCode, setAirportCode] = useState<string | null>(currentAirportCode);
  const [distance, setDistance] = useState<number | null>(currentDistance);
  const [address, setAddress] = useState<string>(currentAddress || "");
  // Task #2609 — remember the geocoded coords for the currently-selected
  // delivery address so we can forward them to POST /bookings. Seeded
  // from props so the parent's last-known selection survives a reopen
  // when the renter doesn't re-pick a suggestion. Cleared whenever the
  // renter clears the selection, picks an airport, or switches back to
  // self-pickup (those paths get coords via different mechanisms on
  // the server side).
  const [latitude, setLatitude] = useState<number | null>(
    currentAirportCode ? null : (currentLatitude ?? null),
  );
  const [longitude, setLongitude] = useState<number | null>(
    currentAirportCode ? null : (currentLongitude ?? null),
  );

  const [inputValue, setInputValue] = useState<string>(
    currentAirportCode ? "" : currentAddress || "",
  );
  const [suggestions, setSuggestions] = useState<Array<{ display_name: string; lat: string; lon: string; airport_code?: string; street?: string; city?: string; state?: string }>>([]);
  const searchSeq = useRef(0);
  const [mobileWeb] = useState(isMobileWeb);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [geocoding, setGeocoding] = useState(false);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suggestionsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  // Cancel any pending debounced address-search timer and the blur-dismiss
  // timer on unmount so their callbacks can't fire after the component is
  // gone (they call setState / touch `window`, which would throw
  // `window is not defined` once the test env tears down).
  useEffect(() => {
    return () => {
      if (suggestionsTimer.current) {
        clearTimeout(suggestionsTimer.current);
        suggestionsTimer.current = null;
      }
      if (blurTimer.current) {
        clearTimeout(blurTimer.current);
        blurTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const isDelivery = currentIsDelivery ?? (!!currentAirportCode || (currentDistance != null && currentDistance > 0));
    setMode(requireDelivery || isDelivery ? "delivery" : "pickup");
    setAirportCode(currentAirportCode);
    setDistance(currentDistance);
    setAddress(currentAddress || "");
    // Task #2609 — seed coords from the parent's last-known selection
    // so that reopening the editor and pressing Apply (without picking
    // a new suggestion) preserves the persisted pin instead of
    // clearing it. Airport delivery never carries coords here.
    setLatitude(currentAirportCode ? null : (currentLatitude ?? null));
    setLongitude(currentAirportCode ? null : (currentLongitude ?? null));
    setInputValue(currentAirportCode ? "" : currentAddress || "");
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null);
  }, [open, currentAirportCode, currentDistance, currentAddress, currentIsDelivery, currentLatitude, currentLongitude, requireDelivery]);

  const useKm = isMetricCountry(countryCode || undefined);
  const unit = getDistanceUnitPlural(countryCode || undefined);

  const fetchSuggestions = (query: string) => {
    if (suggestionsTimer.current) clearTimeout(suggestionsTimer.current);
    const seq = ++searchSeq.current;
    if (query.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    suggestionsTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          addressSearchUrl(query),
        );
        const data = await res.json();
        if (seq !== searchSeq.current) return;
        const results = data.results;
        if (Array.isArray(results) && results.length > 0) {
          setSuggestions(
            results
              .filter(
                (r: Record<string, unknown>) =>
                  typeof r.displayName === "string" &&
                  typeof r.latitude === "number" &&
                  typeof r.longitude === "number",
              )
              .map((r: Record<string, unknown>) => ({
                display_name: r.displayName as string,
                lat: String(r.latitude),
                lon: String(r.longitude),
                airport_code:
                  typeof r.airportCode === "string" ? r.airportCode : undefined,
                street: optionalString(r.street),
                city: optionalString(r.city),
                state: optionalString(r.state),
              })),
          );
          setActiveSuggestionIndex(-1);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
          setActiveSuggestionIndex(-1);
        }
      } catch {
        if (seq !== searchSeq.current) return;
        setSuggestions([]);
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    }, 400);
  };

  const selectAddress = async (
    displayName: string,
    lat: number,
    lon: number,
    airportCodeHint?: string,
  ) => {
    if (carLatitude == null || carLongitude == null) {
      setError(t("booking.delivery_not_available", "Delivery location could not be determined"));
      return;
    }
    searchSeq.current += 1;
    if (suggestionsTimer.current) clearTimeout(suggestionsTimer.current);
    setShowSuggestions(false);
    setSuggestions([]);
    setError(null);

    // Task #4674 / #4794 — when the picked suggestion is a canonical
    // airport from our own table it carries an IATA code. Match the host's
    // configured fee by exact code first; only fall back to fuzzy
    // name/code matching for geocoder results (cities/streets) that have
    // no code. Shared helper so this can't drift from BookingWidget again.
    const matchedAirport = matchDeliveryAirport(
      deliveryAirports,
      displayName,
      airportCodeHint,
    );
    if (matchedAirport) {
      setAirportCode(matchedAirport.airport_code);
      setDistance(null);
      setAddress(`${matchedAirport.airport_name} (${matchedAirport.airport_code})`);
      // Task #2609 — airport delivery routes the host map pin through
      // the airport coords, not the typed address. Clear so we don't
      // accidentally forward stale coords.
      setLatitude(null);
      setLongitude(null);
      setInputValue("");
      return;
    }

    setLoadingDistance(true);
    const { distance: rounded } = await fetchDrivingDistance(
      carLatitude, carLongitude, lat, lon, countryCode || "", useKm,
    );
    setLoadingDistance(false);

    if (deliveryMaxRadius != null && rounded > deliveryMaxRadius) {
      setDistance(null);
      setAirportCode(null);
      setAddress("");
      setLatitude(null);
      setLongitude(null);
      setError(
        t(
          "booking.delivery_too_far",
          "This location is outside the car's delivery range ({{distance}} {{unit}} exceeds the maximum of {{max}} {{unit}}).",
          { distance: rounded, max: deliveryMaxRadius, unit },
        ),
      );
      return;
    }

    setDistance(rounded);
    setAirportCode(null);
    setAddress(displayName);
    // Task #2609 — capture the coords the geocoder/suggestion list
    // already returned so the host map pin renders on the typed
    // address (rather than falling back to the car's home location).
    setLatitude(Number.isFinite(lat) ? lat : null);
    setLongitude(Number.isFinite(lon) ? lon : null);
    setInputValue("");
  };

  const handleGeocode = async (q: string) => {
    if (!q.trim()) return;
    if (carLatitude == null || carLongitude == null) {
      setError(t("booking.delivery_not_available", "Delivery location could not be determined"));
      return;
    }
    setGeocoding(true);
    setError(null);
    setShowSuggestions(false);
    try {
      const res = await fetch(
        `${API_BASE}/address/search?q=${encodeURIComponent(q)}`,
      );
      const data = await res.json();
      const results = data.results;
      if (!Array.isArray(results) || results.length === 0) {
        setError(
          t("booking.address_not_found", "Address not found. Please try a more specific address."),
        );
        return;
      }
      const first = results[0];
      await selectAddress(
        first.displayName || q,
        first.latitude,
        first.longitude,
        typeof first.airportCode === "string" ? first.airportCode : undefined,
      );
    } catch {
      setError(t("booking.geocoding_failed", "Could not look up that address. Please try again."));
    } finally {
      setGeocoding(false);
    }
  };

  const handleApply = () => {
    if (mode === "pickup") {
      onApply({
        deliveryDistance: null,
        deliveryAirportCode: null,
        deliveryAddress: null,
        deliveryLatitude: null,
        deliveryLongitude: null,
      });
      setOpen(false);
      return;
    }
    if (airportCode) {
      onApply({
        deliveryDistance: null,
        deliveryAirportCode: airportCode,
        deliveryAddress: address,
        deliveryLatitude: null,
        deliveryLongitude: null,
      });
      setOpen(false);
      return;
    }
    if (distance != null && Number.isFinite(distance) && distance >= 0) {
      onApply({
        deliveryDistance: distance,
        deliveryAirportCode: null,
        deliveryAddress: address,
        // Task #2609 — forward the geocoded coords (if we have them
        // from this session's address pick) so POST /bookings can
        // persist them on the trip row.
        deliveryLatitude: latitude,
        deliveryLongitude: longitude,
      });
      setOpen(false);
      return;
    }
    setError(isHost
      ? t("checkout.host_location.choose", "Please choose a delivery address or airport, or switch to renter collection if available.")
      : t("checkout.location_choose", "Please pick an address or airport, or switch to free pickup."));
  };

  const pickupLabel =
    [pickupCity, pickupState].filter(Boolean).join(", ") ||
    (isHost
      ? t("checkout.host_location.pickup_location", "Your car’s location")
      : t("checkout.pickup_at_car_location", "Free pickup at the car's location"));

  const isProcessing = geocoding || loadingDistance;
  // Mirror the existing tile permissions for copy, never change eligibility.
  const pickupAvailable = !requireDelivery && allowPickupMode !== false && !(lockMode && mode !== "pickup");
  const deliveryAvailable = deliveryEnabled && allowDeliveryMode !== false && !(lockMode && mode !== "delivery");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={16}
        data-autocomplete-portal-root="checkout-delivery-location"
        onInteractOutside={(event) => {
          const target = event.target as Element | null;
          if (target?.closest('[data-autocomplete-portal="checkout-delivery-location"]')) {
            event.preventDefault();
          }
        }}
        style={{ maxHeight: "min(calc(100dvh - 32px), var(--radix-popover-content-available-height, calc(100dvh - 32px)))" }}
        className="z-[60] pointer-events-auto flex w-[min(96vw,520px)] max-w-[96vw] flex-col overflow-hidden rounded-2xl border border-border bg-popover p-0 shadow-xl"
      >
        <div className="shrink-0 border-b border-border px-5 py-4">
          <p className="text-base font-semibold text-foreground">
            {isHost
              ? t("checkout.host_location.title", "Pick-up & return arrangements")
              : t("checkout.edit_location_title", "Pick-up & return location")}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {isHost
              ? requireDelivery || (!pickupAvailable && mode === "delivery")
                ? t("checkout.host_location.subtitle_delivery_only", "Choose where you’ll deliver your car to the renter. Collection at the car’s location is not available for this change.")
                : !deliveryAvailable
                  ? t("checkout.host_location.subtitle_pickup_only", "The renter will collect and return your car at its location. Delivery is not available for this change.")
                  : t("checkout.host_location.subtitle", "Choose where the renter will collect and return your car, or where you’ll deliver it.")
              : deliveryEnabled
              ? t(
                  "checkout.edit_location_subtitle",
                  "Pick up at the car's location for free, or have it delivered to an address or airport.",
                )
              : t(
                  "checkout.edit_location_subtitle_no_delivery",
                  "This host only offers pickup at the car's location.",
                )}
          </p>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-4"
          data-autocomplete-portal-bounds="checkout-delivery-location"
        >
          {/* Mode toggle ------------------------------------------------ */}
          {lockMode && !requireDelivery && (
            <p
              className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
              data-testid="checkout-location-lockmode-note"
            >
              {isHost ? t("checkout.host_location.lock_mode_note", "You can edit the location within the current arrangement, but cannot switch between renter collection and delivery for this change.") : t(
                "checkout.location_lock_mode_note",
                "To switch between pick-up and delivery, message the owner. Only they can change the delivery fee.",
              )}
            </p>
          )}
          {requireDelivery && (
            <p
              className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
              data-testid="checkout-location-require-delivery-note"
            >
              {requireDeliveryNote ??
                (isHost ? t("checkout.host_location.require_delivery_note", "This rental requires you to deliver the car to the renter.") : t(
                  "checkout.location_require_delivery_note",
                  "Event hires require delivery.",
                ))}
            </p>
          )}
          {modeEligibilityNote && !requireDelivery && (
            <p
              className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
              data-testid="checkout-location-mode-eligibility-note"
            >
              {modeEligibilityNote}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={requireDelivery || allowPickupMode === false || (lockMode && mode !== "pickup")}
              onClick={() => setMode("pickup")}
              data-testid="checkout-location-pickup-tile"
              className={`flex min-w-0 flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
                mode === "pickup"
                  ? "border-foreground bg-muted"
                  : "border-border hover:bg-muted/50"
              } ${requireDelivery || allowPickupMode === false || (lockMode && mode !== "pickup") ? "cursor-not-allowed opacity-50" : ""}`}
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-foreground" />
                <span className="text-sm font-semibold text-foreground">
                  {isHost ? t("checkout.host_location.pickup", "Renter collects") : neutralLabels
                    ? t("checkout.location_pickup_neutral", "Pick up")
                    : t("checkout.location_pickup", "Pick up myself")}
                </span>
              </div>
              <span className="break-words text-xs text-muted-foreground">
                {isHost ? t("checkout.host_location.pickup_hint", "No delivery fee") : t("checkout.free_label", "Free")} · {pickupLabel}
              </span>
            </button>
            <button
              type="button"
              disabled={!deliveryEnabled || allowDeliveryMode === false || (lockMode && mode !== "delivery")}
              onClick={() => setMode("delivery")}
              className={`flex min-w-0 flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
                mode === "delivery"
                  ? "border-foreground bg-muted"
                  : "border-border hover:bg-muted/50"
              } ${!deliveryEnabled || allowDeliveryMode === false || (lockMode && mode !== "delivery") ? "cursor-not-allowed opacity-50" : ""}`}
            >
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 shrink-0 text-foreground" />
                <span className="text-sm font-semibold text-foreground">
                  {isHost ? t("checkout.host_location.delivery", "Deliver to renter") : neutralLabels
                    ? t("checkout.location_delivery_neutral", "Deliver")
                    : t("checkout.location_delivery", "Deliver to me")}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {deliveryEnabled
                  ? isHost
                    ? t("checkout.host_location.delivery_hint", "Address or airport · delivery fee applies")
                    : t("checkout.location_delivery_hint", "Address or airport, fee applies")
                  : t("checkout.location_delivery_unavailable", "Not offered for this car")}
              </span>
            </button>
          </div>

          {mode === "delivery" && deliveryEnabled && (
            <div className="space-y-3">
              {/* Currently selected delivery target (if any) */}
              {(airportCode || distance != null) && (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {airportCode ? (
                      <Truck className="h-4 w-4 shrink-0 text-foreground" />
                    ) : (
                      <MapPin className="h-4 w-4 shrink-0 text-foreground" />
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm text-foreground">
                        {address || (airportCode ?? "")}
                      </p>
                      {distance != null && distance > 0 && !airportCode && (
                        <p className="text-[11px] text-muted-foreground">
                          {t("booking.delivery_distance", "Delivery distance: {{miles}} {{unit}}", {
                            miles: distance,
                            unit,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAirportCode(null);
                      setDistance(null);
                      setAddress("");
                      setInputValue("");
                      setLatitude(null);
                      setLongitude(null);
                    }}
                    className="rounded-full p-1 hover:bg-muted"
                    aria-label={t("checkout.clear_selection", "Clear selection")}
                  >
                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              )}

              {loadingDistance && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Spinner size="sm" />
                  {t("booking.calculating_distance", "Calculating driving distance…")}
                </div>
              )}

              {/* Address input */}
              <div className="relative">
                <label htmlFor="checkout-delivery-address" className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {isHost
                    ? t("checkout.host_location.address_label", "Delivery address or airport")
                    : t("booking.delivery_address", "Enter delivery address")}
                </label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <input
                    id="checkout-delivery-address"
                    ref={addressInputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setActiveSuggestionIndex(-1);
                      fetchSuggestions(e.target.value);
                    }}
                    placeholder={t(
                      "booking.delivery_placeholder",
                      "Street address, city, or airport",
                    )}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown" && suggestions.length > 0) {
                        e.preventDefault();
                        setActiveSuggestionIndex((current) => Math.min(current + 1, suggestions.length - 1));
                        return;
                      }
                      if (e.key === "ArrowUp" && suggestions.length > 0) {
                        e.preventDefault();
                        setActiveSuggestionIndex((current) => Math.max(current - 1, -1));
                        return;
                      }
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const activeSuggestion = showSuggestions && activeSuggestionIndex >= 0
                          ? suggestions[activeSuggestionIndex]
                          : null;
                        if (activeSuggestion) {
                          void selectAddress(
                            activeSuggestion.display_name,
                            parseFloat(activeSuggestion.lat),
                            parseFloat(activeSuggestion.lon),
                            activeSuggestion.airport_code,
                          );
                        } else {
                          setShowSuggestions(false);
                          handleGeocode(e.currentTarget.value);
                        }
                      }
                    }}
                    onBlur={() => {
                      if (blurTimer.current) clearTimeout(blurTimer.current);
                      // Mobile inline list stays in flow so a touch tap on
                      // More results (which blurs first) still works.
                      if (mobileWeb) return;
                      blurTimer.current = setTimeout(
                        () => setShowSuggestions(false),
                        200,
                      );
                    }}
                    disabled={isProcessing}
                    aria-autocomplete="list"
                    aria-controls="checkout-delivery-location-suggestions"
                    aria-expanded={showSuggestions && suggestions.length > 0}
                    aria-activedescendant={
                      showSuggestions && activeSuggestionIndex >= 0
                        ? `checkout-delivery-location-suggestions-option-${activeSuggestionIndex}`
                        : undefined
                    }
                    className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowSuggestions(false);
                      handleGeocode(inputValue);
                    }}
                    disabled={isProcessing || !inputValue.trim()}
                    className="rounded-md bg-foreground px-3 py-2 text-xs font-semibold text-background hover:bg-foreground/90 disabled:opacity-50"
                  >
                    {isProcessing ? <Spinner size="sm" /> : isHost
                      ? t("checkout.host_location.find_location", "Find location")
                      : t("common.go", "Go")}
                  </button>
                </div>
                <MobileAwareAutocomplete
                  anchorRef={addressInputRef}
                  open={showSuggestions}
                  items={suggestions}
                  listId="checkout-delivery-location-suggestions"
                  portalMarker="checkout-delivery-location"
                  portalInDialog
                  portalTargetSelector={'[data-autocomplete-portal-root="checkout-delivery-location"]'}
                  portalBoundsSelector={'[data-autocomplete-portal-bounds="checkout-delivery-location"]'}
                  activeIndex={activeSuggestionIndex}
                  getKey={(suggestion, index) =>
                    `${suggestion.lat}:${suggestion.lon}:${suggestion.airport_code ?? ""}:${index}`
                  }
                  onSelect={(suggestion) => {
                    void selectAddress(
                      suggestion.display_name,
                      parseFloat(suggestion.lat),
                      parseFloat(suggestion.lon),
                      suggestion.airport_code,
                    );
                  }}
                  inlineLimit={mobileWeb ? 3 : undefined}
                  renderItem={(suggestion) => {
                    if (!mobileWeb) return <span className="line-clamp-2">{suggestion.display_name}</span>;
                    const lines = addressSuggestionLines(suggestion);
                    return (
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{lines.primary}</span>
                        {lines.secondary ? <span className="truncate text-sm text-muted-foreground">{lines.secondary}</span> : null}
                      </span>
                    );
                  }}
                />
              </div>

              {/* Airport list */}
              {deliveryAirports.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t("booking.or_select_airport", "Or select an airport:")}
                  </p>
                  {deliveryAirports.map((ap) => {
                    const selected = airportCode === ap.airport_code;
                    return (
                      <button
                        key={ap.airport_code}
                        type="button"
                        onClick={() => {
                          setAirportCode(ap.airport_code);
                          setDistance(null);
                          setAddress(`${ap.airport_name} (${ap.airport_code})`);
                          setInputValue("");
                          setLatitude(null);
                          setLongitude(null);
                          setShowSuggestions(false);
                          setSuggestions([]);
                          setError(null);
                        }}
                        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          selected
                            ? "border-foreground bg-muted"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-foreground">{ap.airport_name}</span>
                          <span className="text-xs text-muted-foreground">({ap.airport_code})</span>
                        </span>
                        <span className="text-xs font-medium text-foreground">
                          {formatPrice(ap.flat_fee, currency)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {error && (
                <Alert variant="warning">
                  <AlertDescription className="text-xs">{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="cursor-pointer rounded-md bg-foreground px-4 py-1.5 text-sm font-semibold text-background hover:bg-foreground/90"
          >
            {t("common.apply", "Apply")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CheckoutLocationEditor;
